import dayjs, { UnitType } from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/en-gb';
import localeData from 'dayjs/plugin/localeData';
import utc from 'dayjs/plugin/utc';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import isBetween from 'dayjs/plugin/isBetween';
import isTomorrow from 'dayjs/plugin/isTomorrow';

export type SupportedDateTypes = Date | number | string | null;
export enum DateDifferenceUnits {
  MILLISECOND = 'millisecond',
  SECOND = 'second',
  MINUTE = 'minute',
  HOUR = 'hour',
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
  YEAR = 'year'
}
export enum DateAdditionUnits {
  SECOND = 'seconds',
  MINUTE = 'minutes',
  HOUR = 'hours',
  DAY = 'days',
  WEEK = 'weeks',
  MONTH = 'months',
  YEAR = 'years'
}
export enum DateFormat {
  SHORT = 'D.M.YYYY',
  MEDIUM = 'D MMM YYYY',
  LONG = 'DD.MM.YYYY',
  UTC_ISO8601 = 'YYYY-MM-DDTHH:mm:ss[Z]',
  DAY = 'ddd'
}
export enum TimeFormat {
  SHORT = 'HH:mm',
  LONG = 'HH:mm:ss'
}

export enum DateTimeFormat {
  DISPLAY = 'D MMM YYYY, HH:mm',
  MEDIUM = 'D MMM YYYY HH:mm'
}

const supportedLocales = {
  en: 'en',
  'en-gb': 'en-gb'
};
const defaultLocale = 'en-gb';
const defaultDateFormat = DateFormat.LONG;
const defaultTimeFormat = TimeFormat.SHORT;

const addTimeZone = (date: SupportedDateTypes): Date | number | string => {
  if (typeof date !== 'string') {
    return date ?? '';
  }

  if (date.includes('Z') || date.includes('+')) {
    return date;
  }

  return `${date}Z`;
};

export const initializeDayJsLocale = async (userLocale?: string) => {
  dayjs.extend(localeData);
  dayjs.extend(localizedFormat);
  dayjs.extend(isBetween);
  dayjs.extend(isTomorrow);

  try {
    const locale = (
      userLocale && Object.hasOwn(supportedLocales, userLocale) ? userLocale : defaultLocale
    ) as keyof typeof supportedLocales;
    dayjs.locale(locale);
  } catch (error) {
    console.warn(`Locale ${userLocale} failed to load, falling back to default.`, error);
    dayjs.locale(defaultLocale);
  }
};

export const formatDate = (date: SupportedDateTypes, dateFormat?: DateFormat | string, useUtc?: boolean): string => {
  if (date) {
    if (useUtc) {
      dayjs.extend(utc);
    }

    const isValid = dayjs(date).isValid() || dayjs(new Date(addTimeZone(date))).isValid();
    const _date = useUtc
      ? dayjs(new Date(addTimeZone(date)).toString()).utc()
      : dayjs(new Date(addTimeZone(date)).toString());

    return isValid ? _date.format(dateFormat ?? defaultDateFormat) : (date as string);
  }

  return '';
};

export const formatTime = (date: SupportedDateTypes, timeFormat?: TimeFormat | string, useUtc?: boolean): string => {
  if (date && date !== null) {
    if (useUtc) {
      dayjs.extend(utc);
    }

    const isValid = dayjs(date).isValid() || dayjs(new Date(addTimeZone(date))).isValid();
    const _date = useUtc
      ? dayjs(new Date(addTimeZone(date)).toString()).utc()
      : dayjs(new Date(addTimeZone(date)).toString());

    return isValid ? _date.format(timeFormat ?? defaultTimeFormat) : (date as string);
  }

  return '';
};

export const formatDateTime = (
  date: SupportedDateTypes,
  format?: DateTimeFormat | string,
  useUtc?: boolean
): string => {
  if (date && date !== null) {
    if (useUtc) {
      dayjs.extend(utc);
    }

    const isValid = dayjs(date).isValid() || dayjs(new Date(addTimeZone(date))).isValid();
    const _date = useUtc
      ? dayjs(new Date(addTimeZone(date)).toString()).utc()
      : dayjs(new Date(addTimeZone(date)).toString());

    return isValid ? _date.format(format ?? defaultDateFormat) : (date as string);
  }

  return '';
};

export const timeDifference = (
  startDate: SupportedDateTypes,
  endDate: SupportedDateTypes,
  differenceType?: UnitType,
  useFloat?: boolean
): number | undefined => {
  if (!startDate || startDate === null || !endDate || endDate === null) {
    return undefined;
  }

  const start = dayjs(new Date(addTimeZone(startDate)).toString()).toISOString(),
    end = dayjs(new Date(addTimeZone(endDate)).toString());

  return end.diff(start, differenceType ?? 'milliseconds', useFloat ?? false);
};

export const dateTimeFullString = (timestamp: number | string): string => {
  return `${formatDate(timestamp)} - ${formatTime(timestamp, TimeFormat.LONG)}`;
};

export const getUtcDate = (
  year: number,
  month: number,
  day: number,
  time?: { hours: number; minutes: number; seconds: number }
): Date => {
  dayjs.extend(utc);

  const date = new Date(year, month, day);

  if (time) {
    date.setHours(time.hours);
    date.setMinutes(time.minutes);
    date.setSeconds(time.seconds);
  }

  return new Date(dayjs(date).utc().format());
};

export const getUtcISOString = (date: Date | undefined): string | undefined => {
  if (!date) {
    return date;
  }

  return dayjs(date).format(DateFormat.UTC_ISO8601);
};

export const dateIsBefore = (startDate: Date, beforeDate: Date, unit?: DateDifferenceUnits): boolean => {
  // without 'unit', the default is milliseconds
  return unit ? dayjs(startDate).isBefore(beforeDate, unit) : dayjs(startDate).isBefore(dayjs(beforeDate));
};

export const dateIsEqual = (
  startDate: Date,
  endDate: Date,
  units: DateDifferenceUnits | DateDifferenceUnits[] = [
    DateDifferenceUnits.DAY,
    DateDifferenceUnits.MONTH,
    DateDifferenceUnits.YEAR
  ]
): boolean => {
  const unitArray: DateDifferenceUnits[] = Array.isArray(units) ? units : [units];
  return unitArray.every((unit) => dayjs(startDate).isSame(endDate, unit));
};

export const dateIsAfter = (startDate: Date, afterDate: Date, unit?: DateDifferenceUnits): boolean => {
  return unit ? dayjs(startDate).isAfter(afterDate, unit) : dayjs(startDate).isAfter(dayjs(afterDate));
};

export const dateIsBetween = (date: Date, startDate: Date, endDate: Date): boolean => {
  return dayjs(date).isBetween(dayjs(startDate), dayjs(endDate), null, '[]');
};

export const dateIsTomorrow = (date: Date): boolean => {
  return dayjs(date).isTomorrow();
};

export const dateIsThisWeek = (date: Date): boolean => {
  return dayjs(date).isSame(dayjs(), DateDifferenceUnits.WEEK);
};

export const dateIsThisMonth = (date: Date): boolean => {
  return dayjs(date).isSame(dayjs(), DateDifferenceUnits.MONTH);
};

export const getExtendedDate = (startDate: Date, amount: number, unit: DateAdditionUnits): Date => {
  return dayjs(startDate).add(amount, unit).toDate();
};
